import {isFeatureEnabled} from 'feature-flags';
import {
  Avatar,
  Card,
  DataStatus,
  FlagProps,
  Flags,
  Separator,
  TabProps,
  Tabs,
  Tooltip,
} from 'platform/components';
import {
  Box,
  Clickable,
  Heading,
  HStack,
  Show,
  Space,
  Stack,
  Text,
  VStack,
} from 'platform/foundation';
import {CurrencyFormat, useFormatCurrency, useFormatNumber} from 'platform/locale';
import {match} from 'ts-pattern';

import {useEffect, useRef} from 'react';

import {defaultTo, find} from 'ramda';
import {concatAll, isFalse, isFalsy, isNotNil, isPositive} from 'ramda-adjunct';

import {EntityResourceIds} from '@dms/api/shared';
import {GetMetadaServiceCaseApiResponse} from '@dms/api/workshop';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {
  Checklists,
  CommentsWidget,
  DEFAULT_PRICE,
  EMPTY_PLACEHOLDER,
  ORDER_TABS,
  queryParams,
  refreshDmsDatagrid,
  useTenant,
  useGetCurrentBranch,
  usePermissions,
} from '@dms/shared';

import {
  buildArray,
  DOT_CHARACTER,
  Nullish,
  RequiredTestIdProps,
  suffixTestId,
  useQueryState,
} from 'shared/web';

import {MetadaNoteWidget} from '../../../../../components/MetadaNoteWidget';
import {useWorkshopUrl} from '../../../../../hooks/useWorkshopUrl';
import {ServiceCaseOrder} from '../../../../../types/OrderType';
import {useOrderCardData} from '../../../hooks/useOrderCardData';
import {useOrderCardFlags} from '../hooks/useOrderCardFlags';
import {OrderDetailFormRef} from '../types/orderDetailFormRef';
import {getPartsMovementsTabIcon} from '../utils/getPartsMovementsTabIcon';
import {ActivityLogDataGrid} from './ActivityLogDataGrid';
import {Checkout} from './Checkout';
import {Discounts} from './Discounts';
import {OrderActions} from './OrderActions';
import {OrderDetail} from './OrderDetail';
import {OrderInformation} from './OrderInformation';
import {OrderItemsDataGrid} from './OrderItemsDataGrid';
import {OrderJobs} from './OrderJobs';
import {PartsMovements} from './PartsMovements';
import {PriceDetailTooltip} from './PriceDetailTooltip';
import {ProfitabilityDataGrid} from './ProfitabilityDataGrid';
import {ProfitabilitySummary} from './ProfitabilitySummary';
import {ServiceOrderDocuments} from './ServiceOrderDocuments';

const OVERPRICED_THRESHOLD = 10;
const PERCENTAGE_MULTIPLIER = 100;
const DIFFERENCE_OFFSET = 1;

interface OrderCardProps extends RequiredTestIdProps {
  defaultServiceOrder: ServiceCaseOrder;
  serviceOrderId: string;
  serviceCase: GetMetadaServiceCaseApiResponse;
  isExpanded: boolean;
  dataGridKey: number;
  onExpand: () => void;
  onDataChange: () => void;
}

export function OrderCard(props: OrderCardProps) {
  const tenant = useTenant();
  const [orderTabId, setOrderTabId] = useQueryState(queryParams.SERVICE_CASE_ORDER_TAB);
  const {serviceCaseId, orderId} = useWorkshopUrl();
  const orderDetailRef = useRef<OrderDetailFormRef>(null);
  const {activeBranchId} = useGetCurrentBranch();

  const formatCurrency = useFormatCurrency();
  const formatNumber = useFormatNumber();

  const {serviceOrder, serviceOrderVariant, integrations, variants, isLoading, isError} =
    useOrderCardData({
      serviceCaseId,
      serviceOrderId: props.serviceOrderId,
      isExpanded: props.isExpanded,
    });

  const [
    canReadChronos,
    canEditServiceOrder,
    canEditSOCustomDiscount,
    canEditSOCustomerContract,
    canReadServiceOrderFinancesProfitability,
  ] = usePermissions({
    permissionKeys: [
      'chronosUser',
      'serviceOrderEdit',
      'serviceOrderCustomDiscountEdit',
      'serviceOrderCustomerContractEdit',
      'serviceOrderFinancesProfitabilityRead',
    ],
    scopes: {
      serviceOrderEdit: {
        branchId: activeBranchId,
        serviceCaseType: serviceOrderVariant?.serviceCaseTypeId ?? '',
        serviceOrderType: serviceOrderVariant?.serviceOrderTypeId ?? '',
      },
      serviceOrderCustomDiscountEdit: {
        branchId: props.serviceCase?.branchId ?? '',
        serviceCaseType: serviceOrderVariant?.serviceCaseTypeId ?? '',
        serviceOrderType: serviceOrderVariant?.serviceOrderTypeId ?? '',
      },
      serviceOrderCustomerContractEdit: {
        branchId: props.serviceCase?.branchId ?? '',
        serviceCaseType: serviceOrderVariant?.serviceCaseTypeId ?? '',
        serviceOrderType: serviceOrderVariant?.serviceOrderTypeId ?? '',
      },
      serviceOrderFinancesProfitabilityRead: {
        branchId: props.serviceCase?.branchId ?? '',
        serviceCaseType: serviceOrderVariant?.serviceCaseTypeId ?? '',
        serviceOrderType: serviceOrderVariant?.serviceOrderTypeId ?? '',
      },
    },
  });

  const handleSaveActionRequest = async () => await orderDetailRef.current?.submit();

  useEffect(() => {
    if (
      isFalse(serviceOrder?.isCheckoutEnabled) &&
      orderTabId === ORDER_TABS.CHECKOUT &&
      serviceOrder?.id === orderId
    ) {
      setOrderTabId(ORDER_TABS.JOBS);
    }
  }, [orderTabId, setOrderTabId, serviceOrder?.isCheckoutEnabled, orderId, serviceOrder?.id]);

  const {stateFlag, markersFlags, timeTrackingFlag, sparePartFlag, isOrderReadOnly} =
    useOrderCardFlags({
      serviceOrder: props.defaultServiceOrder,
      canReadChronos,
      canEditServiceOrder,
      activeBranchId,
      serviceOrderVariant: serviceOrderVariant ?? null,
      serviceCase: props.serviceCase,
    });

  const isReportButtonDisabled =
    integrations?.orderIntegration
      ?.find((integration) => integration?.integration?.code === 'CHRONOS')
      ?.action?.find((action) => action.key === 'chronosContractReport')?.style === 'disabled';

  const type = find(
    (variant) => serviceOrder?.serviceOrderVariantId === variant?.serviceOrderVariantId,
    variants ?? []
  )?.serviceOrderType?.type;

  const tabs: TabProps[] = [
    {
      title: i18n.t('entity.orderRequest.labels.listTitle'),
      id: ORDER_TABS.JOBS,
      'data-testid': suffixTestId('requests', props),
      content: serviceOrder?.id && serviceOrder?.serviceCaseId && (
        <OrderJobs
          data-testid={suffixTestId('jobs', props)}
          orderId={serviceOrder.id}
          orderVariantId={defaultTo(undefined, serviceOrder.serviceOrderVariantId)}
          serviceCaseId={serviceOrder.serviceCaseId}
          isReadOnly={isOrderReadOnly}
          datagridKey={`order-jobs-${props.dataGridKey}`}
          isReportButtonDisabled={isReportButtonDisabled}
        />
      ),
    },
    {
      title: i18n.t('entity.order.labels.detailTitle'),
      id: ORDER_TABS.DETAIL,
      'data-testid': suffixTestId('detail', props),
      content: serviceOrder?.id && serviceOrder?.serviceCaseId && (
        <OrderDetail
          ref={orderDetailRef}
          data-testid={suffixTestId('detail', props)}
          orderId={serviceOrder.id}
          serviceCaseId={serviceOrder.serviceCaseId}
          isReadOnly={isOrderReadOnly}
          serviceOrder={serviceOrder}
          orderVariants={variants}
          orderIntegrations={integrations ?? {orderIntegration: []}}
          orderType={type}
          isLoading={isLoading}
          isError={isError}
        />
      ),
    },
    {
      title: i18n.t('entity.order.labels.discounts'),
      id: ORDER_TABS.DISCOUNTS,
      'data-testid': suffixTestId('discounts', props),
      content: serviceOrder?.id && serviceOrder?.serviceCaseId && (
        <Discounts
          data-testid={suffixTestId('discounts', props)}
          serviceOrderId={serviceOrder.id}
          serviceCaseId={serviceOrder.serviceCaseId}
          isReadOnly={isOrderReadOnly}
          canEditDiscount={canEditSOCustomDiscount}
          canEditCustomerContract={canEditSOCustomerContract}
          orderType={type}
        />
      ),
    },
    {
      title: i18n.t('entity.orderRequest.labels.allItems'),
      id: ORDER_TABS.ALL_ITEMS,
      'data-testid': suffixTestId('items', props),
      content: (
        <OrderItemsDataGrid
          data-testid={suffixTestId('allJobs', props)}
          orderId={serviceOrder?.id ?? ''}
          serviceCaseId={serviceCaseId}
          isReadOnly={isOrderReadOnly}
          key={`order-items-${props.dataGridKey}`}
        />
      ),
    },
    {
      title: i18n.t('general.labels.documents'),
      id: ORDER_TABS.DOCUMENTS,
      'data-testid': suffixTestId('documents', props),
      content: (
        <ServiceOrderDocuments
          refreshDataGrid={() => refreshDmsDatagrid('document-context-list-service-case-order')}
        />
      ),
    },
    {
      title: i18n.t('entity.order.labels.partsMovements'),
      id: ORDER_TABS.PARTS_MOVEMENTS,
      icon: getPartsMovementsTabIcon(serviceOrder?.partsMovements),
      'data-testid': suffixTestId('partsMovements', props),
      content: <PartsMovements orderId={orderId} serviceCaseId={serviceCaseId} />,
    },
    ...(isFeatureEnabled(featureFlags.SALE_TASKS_CHECKLIST) && serviceOrder?.id
      ? [
          {
            title: i18n.t('entity.checklist.labels.title'),
            id: ORDER_TABS.CHECKLISTS,
            content: (
              <Checklists
                recordId={serviceOrder.id}
                resourceId="SERVICE_ORDER"
                contextTarget="service-case-order"
                contextId={serviceOrder.id}
                isReadOnly={isOrderReadOnly}
              />
            ),
            'data-testid': suffixTestId('checklists', props),
          },
        ]
      : []),
    {
      title: i18n.t('entity.order.labels.commentsAndNotes'),
      id: ORDER_TABS.COMMENTS_NOTES,
      'data-testid': suffixTestId('note', props),
      content: (
        <Stack direction={['column', 'column', 'column', 'row']} spacing={4} width="100%">
          <Box flex={1}>
            <CommentsWidget
              cardVariant="inlineWhite"
              resourceId={EntityResourceIds.serviceOrder}
              recordId={orderId}
              isEditDisabled={isOrderReadOnly}
              isAddDisabled={isOrderReadOnly}
              isDeleteDisabled={isOrderReadOnly}
              data-testid={suffixTestId('note', props)}
            />
          </Box>
          <Box flex={1}>
            {serviceOrder?.id && serviceOrder?.serviceCaseId && (
              <MetadaNoteWidget
                resourceId={EntityResourceIds.serviceOrder}
                contextId={serviceOrder.id}
                context="COMMENTS_INTERNAL_NOTE"
                data-testid={suffixTestId('note', props)}
                isEditable={!isOrderReadOnly}
              />
            )}
          </Box>
        </Stack>
      ),
    },
    {
      title: i18n.t('entity.order.labels.profitability'),
      id: ORDER_TABS.PROFITABILITY,
      'data-testid': suffixTestId('profitability', props),
      content: serviceOrder?.id && serviceCaseId && (
        <VStack spacing={4}>
          <ProfitabilitySummary
            isReportButtonDisabled={isReportButtonDisabled}
            serviceCaseId={serviceCaseId}
            canReadServiceOrderFinancesProfitability={canReadServiceOrderFinancesProfitability}
            data-testid={suffixTestId('profitability', props)}
          />
          <Show when={canReadServiceOrderFinancesProfitability}>
            <Heading size={4}>{i18n.t('entity.order.labels.orderProfitability')}</Heading>
            <ProfitabilityDataGrid orderId={serviceOrder?.id ?? ''} serviceCaseId={serviceCaseId} />
          </Show>
        </VStack>
      ),
    },
    {
      title: i18n.t('general.labels.activityLog'),
      id: ORDER_TABS.ACTIVITY_LOG,
      'data-testid': suffixTestId('activityLog', props),
      content: serviceOrder?.id && (
        <ActivityLogDataGrid
          orderId={serviceOrder.id}
          data-testid={suffixTestId('activityLog', props)}
        />
      ),
    },
    {
      title: i18n.t('entity.order.labels.checkout'),
      id: ORDER_TABS.CHECKOUT,
      isDisabled: isFalsy(serviceOrder?.isCheckoutEnabled),
      'data-testid': suffixTestId('checkout', props),
      content: <Checkout data-testid={suffixTestId('checkout', props)} />,
    },
  ];

  const priceDifference = getPriceDifference(
    serviceOrder?.totalPrice?.withVat?.amount,
    serviceOrder?.totalPriceEstimated?.amount
  );
  const isOverpriced = isNotNil(priceDifference) && priceDifference > OVERPRICED_THRESHOLD;

  return (
    <Card colorStripe={getColorStripe(type)}>
      <Clickable onClick={props.onExpand}>
        <HStack spacing={3} align="center">
          <Avatar size="default" variant="square" icon="sidebar/workshop_service_orders" />
          <Box flex={1}>
            <HStack align="flex-start" justify="space-between">
              <HStack spacing={3} align="center">
                <Heading size={3} data-testid={suffixTestId('name', props)}>
                  {defaultTo('-', serviceOrder?.number)}
                </Heading>
                <Flags
                  size="small"
                  spacing={1}
                  flags={concatAll<FlagProps[]>([
                    buildArray<FlagProps>()
                      .when(isNotNil(stateFlag), stateFlag as FlagProps)
                      .when(isNotNil(timeTrackingFlag), timeTrackingFlag!)
                      .when(props.defaultServiceOrder?.isCustomerSyncEnabled, {
                        label: i18n.t('entity.order.labels.differentCustomer'),
                        colorScheme: 'red',
                      })
                      .when(isNotNil(sparePartFlag), sparePartFlag!),
                    markersFlags,
                  ])}
                  data-testid={suffixTestId('state', props)}
                />
              </HStack>
              <OrderActions
                orderId={serviceOrder?.id ?? ''}
                serviceOrder={serviceOrder}
                isExpanded={props.isExpanded}
                onExpand={props.onExpand}
                onDataChange={props.onDataChange}
                onSaveRequest={handleSaveActionRequest}
                refreshServiceOrderDocumentDataGrid={() =>
                  refreshDmsDatagrid('document-context-list-service-case-order')
                }
                isOrderReadOnly={isOrderReadOnly}
                data-testid={props['data-testid']}
              />
            </HStack>
            <Space vertical={1} />
            <HStack align="flex-start" justify="space-between">
              <Tooltip label={i18n.t('entity.order.labels.serviceOrderVariant')}>
                <Text size="small" color="secondary">
                  {serviceOrderVariant?.name || EMPTY_PLACEHOLDER}
                </Text>
              </Tooltip>
              <HStack spacing={1}>
                <Tooltip label={i18n.t('general.labels.estimated')}>
                  <Text
                    size="small"
                    color="secondary"
                    data-testid={suffixTestId('totalPriceEstimated', props)}
                  >
                    {`${formatCurrency(
                      defaultTo(DEFAULT_PRICE, serviceOrder?.totalPriceEstimated?.amount),
                      serviceOrder?.totalPriceEstimated?.currency ?? tenant.tenantCurrency,
                      2
                    )}`}
                  </Text>
                </Tooltip>
                <Text size="small" color="secondary">
                  {DOT_CHARACTER}
                </Text>
                <PriceDetailTooltip
                  materialPrice={props.defaultServiceOrder?.materialSalesPrice}
                  labourPrice={props.defaultServiceOrder?.labourSalesPrice}
                  smallUnitMaterialPrice={props.defaultServiceOrder?.smallUnitMaterialSalesPrice}
                  data-testid={suffixTestId('priceTooltip', props)}
                >
                  <Text
                    size="small"
                    alternative
                    color={isOverpriced ? 'danger' : undefined}
                    data-testid={suffixTestId('totalPriceWithVat', props)}
                  >
                    <CurrencyFormat
                      number={defaultTo(DEFAULT_PRICE, serviceOrder?.totalPrice?.withVat?.amount)}
                      currency={defaultTo(
                        tenant.tenantCurrency,
                        serviceOrder?.totalPrice?.withVat?.currency
                      )}
                      decimals={2}
                    />
                    {isOverpriced && ` (${formatNumber(priceDifference, 1)}%)`}
                  </Text>
                </PriceDetailTooltip>
              </HStack>
            </HStack>
          </Box>
        </HStack>
        <Separator />
        <OrderInformation
          serviceOrder={serviceOrder}
          isReadOnly={isOrderReadOnly}
          data-testid={suffixTestId('contactInfo', props)}
        />
      </Clickable>
      <Show when={props.isExpanded}>
        <Separator />
        <DataStatus isLoading={isLoading} isError={isError} minHeight={56}>
          <Tabs
            variant="condensed"
            onChange={setOrderTabId}
            activeTabId={orderTabId}
            tabs={tabs}
            data-testid={suffixTestId('tabs', props)}
            isLazy
            shouldWrap
            lazyBehavior="unmount"
          />
        </DataStatus>
      </Show>
    </Card>
  );
}

const getPriceDifference = (realPrice: number | Nullish, estimatedPrice: number | Nullish) =>
  isPositive(realPrice) && isPositive(estimatedPrice)
    ? (realPrice / estimatedPrice - DIFFERENCE_OFFSET) * PERCENTAGE_MULTIPLIER
    : null;

const getColorStripe = (type: string | undefined | null) =>
  match(type)
    .with('COM', () => 'blue' as const)
    .with('INS', () => 'magenta' as const)
    .with('WAR', () => 'orange' as const)
    .with('CLA', () => 'red' as const)
    .with('CON', () => 'green' as const)
    .with('INT', () => 'teal' as const)
    .otherwise(() => undefined);
